import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, Camera, Image, CheckCircle, XCircle, Loader2, 
  Receipt, AlertTriangle, Gift, X
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TicketUploader Component
 * Allows users to upload receipt images for AI verification
 */
export const TicketUploader = ({ 
  reservationId, 
  userId,
  onVerificationComplete,
  onClose 
}) => {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];
    if (!validTypes.includes(selectedFile.type)) {
      toast.error(t('ticket_upload.invalid_type'));
      return;
    }

    // Validate file size (max 10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error(t('ticket_upload.file_too_large'));
      return;
    }

    setFile(selectedFile);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(selectedFile);
  };

  const handleUpload = async () => {
    if (!file || !reservationId) return;

    setUploading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('ticket_image', file);
      formData.append('reservation_id', reservationId);

      const response = await axios.post(`${API}/tickets/upload`, formData, {
        headers: {
          'X-User-Id': userId,
          'Content-Type': 'multipart/form-data'
        }
      });

      setResult(response.data);

      if (response.data.verified) {
        toast.success(t('ticket_upload.verified_success', { points: response.data.points_awarded }));
        if (onVerificationComplete) {
          onVerificationComplete(response.data);
        }
      } else if (response.data.success && !response.data.verified) {
        toast.info(t('ticket_upload.needs_review'));
      } else {
        toast.error(response.data.error || t('ticket_upload.verification_failed'));
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || t('ticket_upload.upload_error'));
    } finally {
      setUploading(false);
    }
  };

  const resetUploader = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg max-w-md mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center">
            <Receipt className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">{t('ticket_upload.title')}</h3>
            <p className="text-sm text-gray-500">{t('ticket_upload.subtitle')}</p>
          </div>
        </div>
        {onClose && (
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Result Display */}
      <AnimatePresence mode="wait">
        {result && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={`mb-4 p-4 rounded-xl ${
              result.verified 
                ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800' 
                : result.success 
                  ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
                  : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
            }`}
          >
            <div className="flex items-start gap-3">
              {result.verified ? (
                <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0" />
              ) : result.success ? (
                <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
              ) : (
                <XCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
              )}
              <div>
                <p className={`font-medium ${
                  result.verified 
                    ? 'text-emerald-700 dark:text-emerald-400' 
                    : result.success 
                      ? 'text-amber-700 dark:text-amber-400'
                      : 'text-red-700 dark:text-red-400'
                }`}>
                  {result.verified 
                    ? t('ticket_upload.verified')
                    : result.success 
                      ? t('ticket_upload.pending_review')
                      : t('ticket_upload.not_verified')
                  }
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {result.message || result.error}
                </p>
                {result.points_awarded > 0 && (
                  <div className="flex items-center gap-2 mt-2 text-emerald-600">
                    <Gift className="w-4 h-4" />
                    <span className="font-semibold">+{result.points_awarded} {t('ticket_upload.points')}</span>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upload Area */}
      {!result?.verified && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic"
            onChange={handleFileSelect}
            className="hidden"
            id="ticket-upload"
          />

          {!preview ? (
            <label
              htmlFor="ticket-upload"
              className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl cursor-pointer hover:border-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/10 transition-colors"
            >
              <Upload className="w-10 h-10 text-gray-400 mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                {t('ticket_upload.drop_or_click')}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                JPG, PNG, WEBP, HEIC (max 10MB)
              </p>
            </label>
          ) : (
            <div className="relative">
              <img 
                src={preview} 
                alt="Ticket preview" 
                className="w-full h-48 object-cover rounded-xl"
              />
              <button
                onClick={resetUploader}
                className="absolute top-2 right-2 p-2 bg-white dark:bg-gray-800 rounded-full shadow-lg hover:bg-gray-100"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="flex-1"
              disabled={uploading}
            >
              <Camera className="w-4 h-4 mr-2" />
              {preview ? t('ticket_upload.change_image') : t('ticket_upload.select_image')}
            </Button>
            
            <Button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('ticket_upload.verifying')}
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {t('ticket_upload.verify')}
                </>
              )}
            </Button>
          </div>
        </>
      )}

      {/* Done button after verification */}
      {result?.verified && (
        <Button
          onClick={onClose || resetUploader}
          className="w-full bg-emerald-600 hover:bg-emerald-700"
        >
          <CheckCircle className="w-4 h-4 mr-2" />
          {t('ticket_upload.done')}
        </Button>
      )}

      {/* Tips */}
      <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          <strong>{t('ticket_upload.tips_title')}:</strong>
          <br />
          • {t('ticket_upload.tip_1')}
          <br />
          • {t('ticket_upload.tip_2')}
          <br />
          • {t('ticket_upload.tip_3')}
        </p>
      </div>
    </div>
  );
};

export default TicketUploader;
